import * as THREE from "three";
import { GLTFLoader, MeshoptDecoder } from "three-stdlib";
import { assetUrl } from "@/lib/api";

// A SINGLE shared offscreen WebGL renderer produces PNG snapshots for logo/part
// thumbnails. Previously each thumbnail mounted its own <Canvas> (one WebGL context
// each); opening the logo picker spawned ~20 contexts and evicted the main ring canvas
// (the ring "disappeared"). One shared context + cached data URLs fixes that.
let renderer = null;
let camera = null;
let loader = null;
let decoderReady = null;
const cache = new Map();
let queue = Promise.resolve();

function init() {
  if (renderer) return;
  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  renderer.setSize(160, 160);
  renderer.setPixelRatio(1);
  renderer.setClearColor(0x000000, 0);
  camera = new THREE.PerspectiveCamera(35, 1, 0.01, 100);
  loader = new GLTFLoader();
  const dec = typeof MeshoptDecoder === "function" ? MeshoptDecoder() : MeshoptDecoder;
  decoderReady = dec && dec.ready
    ? dec.ready.then(() => loader.setMeshoptDecoder(dec))
    : Promise.resolve().then(() => loader.setMeshoptDecoder(dec));
}

async function renderOne(modelUrl, orient, color, key) {
  init();
  await decoderReady;
  const gltf = await loader.loadAsync(assetUrl(modelUrl));
  const scene = new THREE.Scene();
  scene.add(new THREE.AmbientLight(0xffffff, 1.5));
  const d1 = new THREE.DirectionalLight(0xffffff, 2.4); d1.position.set(2, 3, 4); scene.add(d1);
  const d2 = new THREE.DirectionalLight(0xffffff, 1.3); d2.position.set(-3, -1, -2); scene.add(d2);

  const obj = gltf.scene.clone(true);
  if (Array.isArray(orient) && (orient[0] || orient[1] || orient[2])) obj.rotation.set(orient[0], orient[1], orient[2]);
  obj.updateMatrixWorld(true);
  obj.traverse((o) => {
    if (o.isMesh && o.geometry) {
      o.geometry = o.geometry.clone();
      o.geometry.computeVertexNormals();
      // Thumbnails have no environment map, so a highly-metallic material renders nearly
      // black. Use a low metalness + higher roughness so the METAL COLOR shows via the
      // scene lights (matches the "color del metal" the customer expects).
      o.material = new THREE.MeshStandardMaterial({ color: new THREE.Color(color), metalness: 0.3, roughness: 0.5, side: THREE.DoubleSide });
    }
  });

  const box = new THREE.Box3().setFromObject(obj);
  const size = new THREE.Vector3(); box.getSize(size);
  const ctr = new THREE.Vector3(); box.getCenter(ctr);
  obj.position.sub(ctr);
  scene.add(obj);

  const maxDim = Math.max(size.x, size.y, size.z) || 1;
  camera.position.set(maxDim * 0.4, maxDim * 0.3, maxDim * 2.2);
  camera.lookAt(0, 0, 0);
  renderer.render(scene, camera);
  const url = renderer.domElement.toDataURL("image/png");

  scene.traverse((o) => { o.geometry && o.geometry.dispose && o.geometry.dispose(); o.material && o.material.dispose && o.material.dispose(); });
  cache.set(key, url);
  return url;
}

export function getGlbThumb(modelUrl, orient, color = "#C9CAD1") {
  const key = modelUrl + "|" + (orient || []).join(",") + "|" + color;
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  // Per-render timeout so a single slow/broken asset never permanently stalls the shared
  // serial queue (which previously left every later thumbnail spinning forever).
  const withTimeout = () => Promise.race([
    renderOne(modelUrl, orient, color, key),
    new Promise((res) => setTimeout(() => res(null), 12000)),
  ]).catch(() => null);
  queue = queue.then(withTimeout);
  return queue;
}
